import React, { useContext, useEffect, useState } from "react";
import { Modal, Upload, Input, Select, message, Radio } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import useAxiosInstance from "../../lib/useAxiosInstance";
import PlaybookContext from "../../context/playbookContext";

const { Dragger } = Upload;

const ELECTION_SCOPE_OPTIONS = [
  { value: "state", label: "State (multiple assemblies)" },
  { value: "parliamentary", label: "Parliamentary" },
  { value: "assembly", label: "Assembly" },
];

const REPORT_KIND_OPTIONS = {
  state: [{ value: "assembly", label: "Assembly playbook" }],
  parliamentary: [
    { value: "parliamentary", label: "Whole parliamentary report" },
    { value: "assembly", label: "Single assembly playbook" },
  ],
  assembly: [
    { value: "assembly", label: "Whole assembly report" },
    { value: "block", label: "Block playbook" },
  ],
};

const PlaybookUploadForm = ({ isOpen, onClose, onSaved }) => {
  const axiosInstance = useAxiosInstance();
  const playbookContext = useContext(PlaybookContext);

  const isAddMode = playbookContext.isAddMode;
  const editData = playbookContext.editData;

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("piu");
  const [type, setType] = useState("village"); // PIU sub-type
  const [electionScope, setElectionScope] = useState("assembly");
  const [reportKind, setReportKind] = useState("assembly");
  const [constituency, setConstituency] = useState("");
  const [block, setBlock] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    fetchClients();

    if (!isAddMode && editData?._id) {
      setName(editData.name || "");
      setCategory(editData.category || "piu");
      setType(editData.type || "village");
      setElectionScope(editData.electionScope || "assembly");
      setReportKind(editData.reportKind || "assembly");
      setConstituency(editData.constituency || "");
      setBlock(editData.block || "");
      const existingClientIds = (
        editData.clients?.length
          ? editData.clients
          : editData.client
          ? [editData.client]
          : []
      ).map((c) => (typeof c === "string" ? c : c?._id));
      setSelectedClientIds(existingClientIds.filter(Boolean));
      setFileList([]);
    } else {
      resetForm();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep reportKind valid whenever the election scope changes.
  useEffect(() => {
    const validKinds = REPORT_KIND_OPTIONS[electionScope] || [];
    if (!validKinds.some((k) => k.value === reportKind)) {
      setReportKind(validKinds[0]?.value || "assembly");
    }
  }, [electionScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const res = await axiosInstance.get("api/playbook/clients");
      setClients(res.data?.data || []);
    } catch (error) {
      message.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const resetForm = () => {
    setName("");
    setCategory("piu");
    setType("village");
    setElectionScope("assembly");
    setReportKind("assembly");
    setConstituency("");
    setBlock("");
    setSelectedClientIds([]);
    setFileList([]);
  };

  const handleCancel = () => {
    resetForm();
    onClose && onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      message.error("Playbook name is required");
      return;
    }
    if (!selectedClientIds.length) {
      message.error("Please select at least one client");
      return;
    }
    if (isAddMode && !fileList.length) {
      message.error("Please upload a JSON file");
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("name", name);
      formData.append("category", category);
      formData.append("clientIds", JSON.stringify(selectedClientIds));

      if (category === "piu") {
        formData.append("type", type);
      } else {
        formData.append("electionScope", electionScope);
        formData.append("reportKind", reportKind);
        if (constituency.trim()) formData.append("constituency", constituency.trim());
        if (block.trim()) formData.append("block", block.trim());
      }

      if (fileList.length) {
        formData.append("file", fileList[0].originFileObj || fileList[0]);
      }

      if (isAddMode) {
        await axiosInstance.post("api/playbook", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success("Playbook created successfully");
      } else {
        await axiosInstance.put(`api/playbook/${editData._id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success("Playbook updated successfully");
      }

      onSaved && onSaved();
      resetForm();
      onClose && onClose();
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "Something went wrong";
      message.error(backendMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadProps = {
    beforeUpload: () => false,
    fileList,
    accept: ".json",
    maxCount: 1,
    onChange: ({ fileList: next }) => setFileList(next),
    onRemove: () => setFileList([]),
  };

  const getClientLabel = (client) => {
    const fullName = `${client.adminFirstName || ""} ${client.adminLastName || ""}`.trim();
    return fullName || client.organisationName || client.adminEmail || client._id;
  };

  const showBlockField = category === "electionMode" && reportKind === "block";
  const showConstituencyField =
    category === "electionMode" && (reportKind === "assembly" || reportKind === "block");

  return (
    <Modal
      className="share-report-modal edit-reports"
      open={isOpen}
      onCancel={handleCancel}
      width={760}
      maskClosable={false}
      okText={isAddMode ? "Save" : "Update"}
      onOk={handleSubmit}
      confirmLoading={submitting}
    >
      <div className="modal-body-container">
        <div className="modal-title">
          <h3>{isAddMode ? "Upload Playbook JSON" : "Edit Playbook"}</h3>
        </div>

        <div className="modal-wrapper-body">
          <div className="modal-wrapper-content">
            <div className="add-modal-row">
              <label>Category</label>
              <Radio.Group
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="piu">PIU</Radio.Button>
                <Radio.Button value="electionMode">Election Mode</Radio.Button>
              </Radio.Group>
            </div>

            {category === "piu" ? (
              <div className="add-modal-row">
                <label>Type</label>
                <Radio.Group
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="constituency">Constituency</Radio.Button>
                  <Radio.Button value="village">Village</Radio.Button>
                </Radio.Group>
              </div>
            ) : (
              <>
                <div className="add-modal-row">
                  <label>Election Mode Scope</label>
                  <Radio.Group
                    value={electionScope}
                    onChange={(e) => setElectionScope(e.target.value)}
                    buttonStyle="solid"
                  >
                    {ELECTION_SCOPE_OPTIONS.map((opt) => (
                      <Radio.Button key={opt.value} value={opt.value}>
                        {opt.label}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </div>

                <div className="add-modal-row">
                  <label>JSON represents</label>
                  <Radio.Group
                    value={reportKind}
                    onChange={(e) => setReportKind(e.target.value)}
                    buttonStyle="solid"
                  >
                    {(REPORT_KIND_OPTIONS[electionScope] || []).map((opt) => (
                      <Radio.Button key={opt.value} value={opt.value}>
                        {opt.label}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </div>

                {showConstituencyField && (
                  <div className="add-modal-row">
                    <label>Constituency / AC Name (optional — auto-detected from JSON if blank)</label>
                    <Input
                      placeholder="e.g. Ram Nagar AC"
                      value={constituency}
                      onChange={(e) => setConstituency(e.target.value)}
                    />
                  </div>
                )}

                {showBlockField && (
                  <div className="add-modal-row">
                    <label>Block Name (optional — auto-detected from JSON if blank)</label>
                    <Input
                      placeholder="e.g. Ram Nagar Block"
                      value={block}
                      onChange={(e) => setBlock(e.target.value)}
                    />
                  </div>
                )}
              </>
            )}

            <div className="add-modal-row">
              <label>Playbook Name</label>
              <Input
                placeholder="Enter playbook name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="add-modal-row">
              <label>Clients (visible to)</label>
              <Select
                mode="multiple"
                className="custom-dropdown"
                style={{ width: "100%" }}
                placeholder="Select one or more clients"
                showSearch
                value={selectedClientIds}
                onChange={setSelectedClientIds}
                loading={loadingClients}
                filterOption={(input, option) =>
                  option?.children
                    ?.toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {clients.map((client) => (
                  <Select.Option key={client._id} value={client._id}>
                    {getClientLabel(client)}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="add-modal-row">
              <label>JSON File {!isAddMode && "(leave empty to keep existing)"}</label>
              <Dragger {...uploadProps}>
                <p className="ant-upload-drag-icon">
                  <FileTextOutlined />
                </p>
                <p className="ant-upload-text">Click here to upload or drag & drop</p>
                <p className="ant-upload-hint">Only .json files allowed</p>
              </Dragger>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PlaybookUploadForm;
